// STORE02 offline launch. Owner: M2 offline shell. Registers the app-shell service worker (sw.ts) and, when
// /api/session cannot be reached, stands in a minimal session rebuilt from the names of this device's existing
// 'tuned:<device>:<profile>:<connection>' databases, so the Shell renders the cached catalog, favorites and history
// from IndexedDB instead of "could not start". Nothing new is persisted: no session, credential or /api response is
// stored anywhere. OfflineBanner says so honestly: saved data only, streams need a connection.
import { useCallback, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { Connection, SessionResponse } from '@tuned/contracts';
import { ApiRequestError } from '../app/api.ts';
import { sessionKey } from '../app/session.ts';

let standIn: SessionResponse | null = null;
const isStandIn = (qc: QueryClient) => standIn !== null && qc.getQueryData(sessionKey) === standIn;

/** Registers /sw.js (production and VITE_E2E builds only; never `vite dev`) and the offline session stand-in. */
export function registerServiceWorker(qc: QueryClient): void {
  if (!import.meta.env.PROD) return;
  if ('serviceWorker' in navigator) {
    addEventListener('load', () => void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {}), { once: true });
  }
  const standInSession = async () => {
    if (qc.getQueryData(sessionKey)) return;
    const s = await offlineSession();
    if (s && !qc.getQueryData(sessionKey)) {
      standIn = s;
      qc.setQueryData(sessionKey, s, { updatedAt: 0 }); // stale: every mount retries the real session in the background
    }
  };
  if (!navigator.onLine) void standInSession();
  qc.getQueryCache().subscribe(({ type, query }) => {
    // Only a network failure: an HTTP error (401 signed out, 5xx) is an answer and keeps its normal handling.
    if (type === 'updated' && query.queryKey[0] === sessionKey[0] && query.state.status === 'error' && query.state.data === undefined && !(query.state.error instanceof ApiRequestError)) void standInSession();
  });
  // Back online: drop the stand-in so the real session (or a 401 → setup) replaces it.
  addEventListener('online', () => { if (isStandIn(qc)) void qc.resetQueries({ queryKey: sessionKey }); });
}

/**
 * The single device/profile with a local database, or null (none, shared-device memory mode, or several profiles —
 * never guess whose history to show). Unknown fields get honest, restrictive values: PIN-gated content stays locked.
 */
export async function offlineSession(): Promise<SessionResponse | null> {
  if (typeof indexedDB === 'undefined' || !indexedDB.databases) return null;
  // Bounded (Chrome can hold databases() while an open/delete is pending), but generously: this runs once at launch.
  const list = await Promise.race([indexedDB.databases(), new Promise<IDBDatabaseInfo[]>((r) => setTimeout(() => r([]), 5000))]).catch(() => []);
  // Only connection-scoped databases hold a catalog; ':none' ones (pre-connect, or a device replaced at connect) are ignored.
  const scopes = list.map((d) => (d.name ?? '').split(':')).filter((p) => p.length === 4 && p[0] === 'tuned' && p[3] !== 'none');
  const owners = new Set(scopes.map(([, device, profile]) => `${device}:${profile}`));
  if (owners.size !== 1) return null;
  const [, deviceId, profileId] = scopes[0];
  const connectionIds = [...new Set(scopes.map((p) => p[3]))];
  const epoch = new Date(0).toISOString();
  const connection = (id: string): Connection => ({
    id, alias: 'Offline', serverDisplay: 'Unavailable offline', insecureTransport: false, adapter: 'xtream-player-api', remembered: false,
    sessionState: 'connected', sessionExpiresAt: null, rememberedUntil: null, userConnectionAllowance: null, capabilities: [],
    account: { status: 'unknown', expiresAt: null, maxConnections: null, activeConnections: null, allowedOutputFormats: [], isTrial: null, serverTimezone: null, serverTime: null, message: null },
    catalog: {
      revision: 'offline', status: 'stale', lastSuccessAt: null, lastAttemptAt: null, counts: { live: 0, movie: 0, series: 0, program: 0 },
      guide: { status: 'stale', coverageFrom: null, coverageTo: null, lastSuccessAt: null }, error: null,
    },
  });
  return {
    device: { id: deviceId, sharedMode: false, createdAt: epoch },
    profile: { id: profileId, name: '', pinSet: true, restrictedUnlocked: false, childMode: false },
    connections: connectionIds.map(connection),
  };
}

function subscribe(qc: QueryClient, cb: () => void) {
  addEventListener('online', cb);
  addEventListener('offline', cb);
  const unsub = qc.getQueryCache().subscribe(cb);
  return () => { removeEventListener('online', cb); removeEventListener('offline', cb); unsub(); };
}

/** True while the browser is offline or the Shell runs on the offline session stand-in. */
export function useOffline(): boolean {
  const qc = useQueryClient();
  const sub = useCallback((cb: () => void) => subscribe(qc, cb), [qc]);
  return useSyncExternalStore(sub, () => !navigator.onLine || isStandIn(qc), () => false);
}

/** Global offline notice for the Shell (UX: honest unavailability, never implies streams work offline). */
export function OfflineBanner() {
  if (!useOffline()) return null;
  return (
    <div className="shell__banner" role="status" data-testid="offline-banner">
      <p><strong>You’re offline.</strong> Showing favorites, history and catalog saved on this device; they may be out of date. Streams need a connection to play.</p>
    </div>
  );
}
